import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import 'leaflet/dist/leaflet.css'
import type L from 'leaflet'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store'
import { useTranslation } from '@/translations/i18n'
import { useSubgeoCacheInvalidation } from '../hooks/use-subgeo-cache-invalidation'
import { useNeighborhoodColorFunction } from '../hooks/use-subgeo-colors'
import type { Neighborhood, Subgeo } from '../types'
import {
	buildGeoJSONFeatureCollection,
	createGeoJSONLayer,
	createNeighborhoodDataMap,
	createStyleFunction,
	createSubgeoLookupMap,
	filterValidNeighborhoods,
	fitMapToBounds,
	initializeLeafletMap,
	type MapEventHandlers,
	updateLayerStyles,
	zoomToNeighborhood
} from '../utils/leaflet-map-utils'
import {
	createAggregateCache,
	createTooltipCache,
	createTooltipFactory
} from '../utils/tooltip-utils'
import { NeighborhoodContextMenu } from './neighborhood-context-menu'
import { cleanupTooltipRoots, destroyAllTooltips } from './neighborhood-tooltip'

interface LeafletMapProps {
	neighborhoods: Neighborhood[]
	selectedIds: Set<string>
	toggleNeighborhood: (id: string) => void
	shouldZoomToFit?: boolean
	subgeos: Subgeo[]
	zoomToNeighborhoodId?: string | null
}

export const LeafletMap = ({
	neighborhoods,
	selectedIds,
	toggleNeighborhood,
	shouldZoomToFit,
	subgeos,
	zoomToNeighborhoodId
}: LeafletMapProps) => {
	const mapRef = useRef<HTMLDivElement | null>(null)
	const mapInstance = useRef<L.Map | null>(null)
	const layerRef = useRef<L.GeoJSON | null>(null)
	const initializedRef = useRef(false)
	const tooltipCache = useRef(createTooltipCache())
	const aggregateCache = useRef(createAggregateCache())

	const editingSubgeoId = useSubgeoStore((state) => state.editingSubgeoId)
	const { t } = useTranslation()

	const [contextMenuState, setContextMenuState] = useState<{
		x: number
		y: number
		neighborhoodId: string
		neighborhoodName: string
	} | null>(null)

	const getNeighborhoodColor = useNeighborhoodColorFunction(
		subgeos,
		editingSubgeoId
	)

	const subgeoLookup = useMemo(
		() => createSubgeoLookupMap(subgeos),
		[subgeos]
	)
	const neighborhoodDataMap = useMemo(
		() => createNeighborhoodDataMap(neighborhoods),
		[neighborhoods]
	)
	const validNeighborhoods = useMemo(
		() => filterValidNeighborhoods(neighborhoods),
		[neighborhoods]
	)
	const geoJSONFeatureCollection = useMemo(
		() => buildGeoJSONFeatureCollection(validNeighborhoods),
		[validNeighborhoods]
	)

	const styleFunction = useMemo(
		() => createStyleFunction(selectedIds, getNeighborhoodColor),
		[selectedIds, getNeighborhoodColor]
	)

	const getStyleForId = useCallback(
		(id: string) => {
			const isSelected = selectedIds.has(id)
			const colors = getNeighborhoodColor(id, isSelected)
			return {
				fillColor: colors.fill,
				fillOpacity: colors.fillOpacity,
				color: colors.border,
				weight: 1
			}
		},
		[selectedIds, getNeighborhoodColor]
	)

	const createTooltip = useMemo(
		() =>
			createTooltipFactory(
				neighborhoodDataMap,
				subgeoLookup,
				t as (key: string, options?: Record<string, string>) => string,
				tooltipCache.current,
				aggregateCache.current
			),
		[neighborhoodDataMap, subgeoLookup, t]
	)

	useSubgeoCacheInvalidation(
		subgeos,
		tooltipCache.current,
		aggregateCache.current,
		subgeoLookup
	)

	const eventHandlers: MapEventHandlers = useMemo(
		() => ({
			onNeighborhoodClick: toggleNeighborhood,
			onNeighborhoodContextMenu: (id, name, x, y) => {
				setContextMenuState({
					x,
					y,
					neighborhoodId: id,
					neighborhoodName: name
				})
			},
			onTooltipRequest: createTooltip
		}),
		[toggleNeighborhood, createTooltip]
	)

	useEffect(() => {
		if (!mapRef.current || mapInstance.current) {
			return
		}

		mapInstance.current = initializeLeafletMap(mapRef.current)

		return () => {
			destroyAllTooltips()
			mapInstance.current?.remove()
			mapInstance.current = null
		}
	}, [])

	useEffect(() => {
		const map = mapInstance.current
		if (!(map && geoJSONFeatureCollection)) {
			return
		}

		if (layerRef.current) {
			cleanupTooltipRoots()
			tooltipCache.current.clear()
			aggregateCache.current.clear()
			map.removeLayer(layerRef.current)
			layerRef.current = null
		}

		if (validNeighborhoods.length === 0) {
			console.warn('No valid neighborhoods found')
			return
		}

		const layer = createGeoJSONLayer(
			geoJSONFeatureCollection,
			styleFunction,
			eventHandlers,
			editingSubgeoId
		)

		layer.addTo(map)
		layerRef.current = layer

		if (!initializedRef.current) {
			initializedRef.current = true
		}
	}, [
		geoJSONFeatureCollection,
		validNeighborhoods.length,
		styleFunction,
		eventHandlers,
		editingSubgeoId
	])

	useEffect(() => {
		const map = mapInstance.current
		const layer = layerRef.current
		if (!(map && layer && shouldZoomToFit)) {
			return
		}

		fitMapToBounds(map, layer)
	}, [shouldZoomToFit])

	useEffect(() => {
		const map = mapInstance.current
		const layer = layerRef.current
		if (!(map && layer && zoomToNeighborhoodId)) {
			return
		}

		zoomToNeighborhood(map, layer, zoomToNeighborhoodId)
	}, [zoomToNeighborhoodId])

	useEffect(() => {
		const layer = layerRef.current
		if (!layer) {
			return
		}

		requestAnimationFrame(() => {
			updateLayerStyles(layer, getStyleForId)
		})
	}, [getStyleForId])

	return (
		<>
			<div className='h-full w-full' ref={mapRef} />

			<NeighborhoodContextMenu
				contextMenuState={contextMenuState}
				onClose={() => setContextMenuState(null)}
				subgeos={subgeos}
			/>
		</>
	)
}
